import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LogOut, Image, Video, MessageSquare, Settings, 
  BarChart, FileText
} from 'lucide-react';
import axios from 'axios';
import { Toaster } from 'react-hot-toast';
import ContentManagerModern from '../components/admin/ContentManagerModern';
import GalleryModern from '../components/admin/GalleryModern';
import DashboardModern from '../components/admin/DashboardModern';
import TestimonialsModern from '../components/admin/TestimonialsModern';
import SettingsManager from '../components/admin/SettingsManager';
import './Admin.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  const getAuthHeader = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { headers: { Authorization: `Bearer ${token}` } };
  }, []);

  const verifyAuth = useCallback(async () => {
    try {
      await axios.get(`${API}/verify`, getAuthHeader());
      setLoading(false);
    } catch (error) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_username');
      navigate('/admin');
    }
  }, [getAuthHeader, navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  const loadStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/dashboard/stats`, getAuthHeader());
      setStats(response.data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [getAuthHeader]);

  const loadTestimonials = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/testimonials`, getAuthHeader());
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    }
  }, [getAuthHeader]);

  // Add missing gallery state and load function
  const [galleryItems, setGalleryItems] = useState([]);
  
  const loadGallery = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/gallery`, getAuthHeader());
      setGalleryItems(response.data);
    } catch (error) {
      console.error('Error loading gallery:', error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    verifyAuth();
  }, [verifyAuth]);

  useEffect(() => {
    if (activeTab === 'dashboard') loadStats();
    if (activeTab === 'gallery') loadGallery();
    if (activeTab === 'testimonials') loadTestimonials();
  }, [activeTab, loadStats, loadTestimonials, loadGallery]);

  if (loading) {
    return <div className="admin-loading">Chargement...</div>;
  }

  return (
    <div className="admin-dashboard-page">
      <Toaster />
      {/* Sidebar */}
      <div className="admin-sidebar">
        <div className="admin-sidebar-header">
          <img 
            src="https://customer-assets.emergentagent.com/job_cyga-refresh/artifacts/iagmxpji_Cyga%20events%20Visuel%20Site-02.png" 
            alt="Logo" 
            className="admin-sidebar-logo"
          />
          <h2>Admin Panel</h2>
          <p className="admin-user">{localStorage.getItem('admin_username')}</p>
        </div>

        <nav className="admin-nav">
          <button
            className={`admin-nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            <BarChart size={20} /> Dashboard
          </button>
          <button
            className={`admin-nav-btn ${activeTab === 'gallery' ? 'active' : ''}`}
            onClick={() => setActiveTab('gallery')}
          >
            <Image size={20} /> Galerie
          </button>
          <button
            className={`admin-nav-btn ${activeTab === 'testimonials' ? 'active' : ''}`}
            onClick={() => setActiveTab('testimonials')}
          >
            <MessageSquare size={20} /> Témoignages
          </button>
          <button
            className={`admin-nav-btn ${activeTab === 'content' ? 'active' : ''}`}
            onClick={() => setActiveTab('content')}
          >
            <FileText size={20} /> Contenu & Médias
          </button>
          <button
            className={`admin-nav-btn ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => setActiveTab('settings')}
          >
            <Settings size={20} /> Paramètres
          </button>
        </nav>

        <div className="admin-sidebar-footer">
          <button onClick={handleLogout} className="admin-logout-button">
            <LogOut size={20} /> Déconnexion
          </button>
          <button onClick={() => navigate('/')} className="admin-back-button">
            ← Retour au site
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="admin-content">
        {activeTab === 'dashboard' && (
          <DashboardModern 
            stats={stats} 
            testimonials={testimonials}
            getAuthHeader={getAuthHeader} 
          />
        )}

        {activeTab === 'gallery' && (
          <GalleryModern getAuthHeader={getAuthHeader} />
        )}

        {activeTab === 'content' && (
          <ContentManagerModern getAuthHeader={getAuthHeader} />
        )}

        {activeTab === 'testimonials' && (
          <TestimonialsModern 
            testimonials={testimonials}
            getAuthHeader={getAuthHeader}
            onUpdate={loadTestimonials}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsManager getAuthHeader={getAuthHeader} />
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
